import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import Divider from "@mui/material/Divider";
import Collapse from "@mui/material/Collapse";
import Box from "@mui/material/Box";
import { useCallback, useEffect, useMemo, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Virtuoso } from "react-virtuoso";

const getHashKey = (level, id) => `${level}-${id}`;

export function ConfigureControl({
  item,
  hash,
  level,
  onHashChange,
  parentId,
  toggleCollaps
}) {
  const [data, setData] = useState([]);

  const hashItem = useMemo(
    () => hash[getHashKey(level, item.id)],
    [hash, level, item]
  );
  const isChecked = useMemo(() => !!hashItem, [hashItem]);
  const itemValue = useMemo(
    () => hashItem || { ...item, level, parentId, displayOrder: "" },
    [hashItem, item, level, parentId]
  );

  const padding = useMemo(() => {
    return 2 * level - 1;
  }, [level]);

  const isAllChecked = useMemo(
    () =>
      data.length ===
      data.filter((item) => hash[getHashKey(level + 1, item.id)]).length,
    [data, hash, level]
  );

  const toggleChecked = useCallback(() => {
    onHashChange([itemValue], !isChecked);
    if (level === 2) {
      toggleCollaps(!isChecked);
    }
  }, [isChecked, itemValue, onHashChange, toggleCollaps, level]);

  const handleCheckedAll = useCallback(() => {
    const items = data.map((child) => {
      const l = level + 1;
      return {
        ...(hash[getHashKey(level + 1, child.id)] || {}),
        ...child,
        parentId: item.id,
        level: l,
      };
    });
    if (!isChecked && !isAllChecked) {
      items.push(itemValue);
    }
    onHashChange(items, !isAllChecked);
  }, [data, isAllChecked, isChecked, onHashChange, level, hash, item.id, itemValue]);

  const handleChildrenChange = useCallback(
    (items, state) => {
      if (!state) return onHashChange(items, state);
      const newItems = [...items, itemValue];
      onHashChange(newItems, state);
    },
    [onHashChange, itemValue]
  );

  useEffect(() => {
    if (item.children) {
      setData(item.children);
    }
  }, [item.children]);

  return (
    <Box>
      <Box sx={{ width: '100%', display: 'flex' }}>
        <List
          disablePadding
          sx={{
            background: "#f9f9f9",
            display: "flex",
            alignItems: "center",
            width: "100%"
          }}
        >
          <Divider />
          <ListItemButton
            sx={{
              paddingLeft: padding,
              display: "flex",
              justifyContent: "space-between",
            }}
            onClick={() => toggleCollaps(!item.isCollapsed)}
          >
            <Box sx={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
              {data.length ? (
                item.isCollapsed ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )
              ) : (
                <div style={{ paddingRight: "24px" }}></div>
              )}
              <FormControlLabel
                label={item.name}
                onClick={(e) => e.stopPropagation()}
                control={
                  <Checkbox
                    name={item.id.toString()}
                    checked={isChecked}
                    onChange={toggleChecked}
                  />
                }
              />
            </Box>
            {item.isCollapsed && level < 3 && (
              <FormControlLabel
                label="Enable all"
                sx={{
                  fontSize: "14px",
                }}
                onClick={(e) => e.stopPropagation()}
                control={
                  <Checkbox
                    name={item.id.toString()}
                    sx={{
                      fontSize: "14px",
                    }}
                    checked={isAllChecked}
                    onChange={handleCheckedAll}
                  />
                }
              />
            )}
          </ListItemButton>
        </List>
      </Box>
      <Collapse in={item.isCollapsed && level !== 3}>
        {item.isCollapsed &&
          <Virtuoso
            data={data}
            style={{ height: data.length > 7 ? '350px' : `${data.length * 58}px` }}
            itemContent={(_, child) => <ConfigureControl
              key={child.id}
              hash={hash}
              item={child}
              level={level + 1}
              onHashChange={handleChildrenChange}
              parentId={item.id}
            />}
          />
        }
      </Collapse>
    </Box>
  );
}
